import { FC, useEffect } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { Header, Layout, NextButton } from '@eneco/flows/components';
import { useFlowRedirect } from '@eneco/flows/hooks';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { NotificationBox, RadioGroup, RadioTile } from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import { GenericRadioChoiceStepRendering } from '@sitecore/types/GenericRadioChoiceStep';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { FlowContextSme } from '../../context';

interface FormFields {
  revenue: string;
}

export const RevenueStep: FC = () => {
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<FlowContextSme>();
  const { send: sendToFlowMachine } = useFlowActorRef();
  const flowContext = useFlowSelector(state => state.context);
  const { customRedirect } = useFlowRedirect();

  const { GenericRadioChoiceStep: { fields: textualData } = {} } = usePlaceholderContent<{
    GenericRadioChoiceStep: GenericRadioChoiceStepRendering;
  }>();

  const formSchema = yup.object({
    revenue: yup.string().required(textualData?.choice?.genericContentRadio?.value?.requiredMessage),
  });

  const {
    handleSubmit,
    control,
    getValues,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onSubmit',
    defaultValues: {
      revenue: flowContext.revenue,
    },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  useEffect(() => {
    if (isSubmitSuccessful) {
      const formData = getValues();
      const revenueValue = formData.revenue;

      // Check if business qualifies for grotzakelijk
      // Redirect if: revenue is "twoMillionOrMore"
      if (revenueValue === 'twoMillionOrMore') {
        const selectedOption = textualData?.choice?.genericContentRadio?.value?.options?.find(
          (option: { name?: string }) => option.name === revenueValue,
        ) as { name: string; label: string; illustration: string; redirectLink?: { href: string } } | undefined;
        const redirectLink = selectedOption?.redirectLink;

        if (redirectLink?.href) {
          customRedirect(redirectLink.href, {
            queryParams: {
              postalCode: flowContext.postalCode,
              houseNumber: flowContext.houseNumber,
              houseNumberSuffix: flowContext.houseNumberSuffix,
            },
            cleanBasket: false,
          });
          return;
        }
      }

      // Otherwise continue with SME flow
      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: {
          revenue: revenueValue,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  return (
    <Layout.Content variant="A" handleSubmit={handleSubmit}>
      <Header>
        <Header.Title>{textualData?.content?.title?.value}</Header.Title>
        {textualData?.content?.content?.value && <RichText html={textualData.content.content.value} />}
      </Header>

      {textualData?.content?.notification?.value?.content && (
        <NotificationBox
          isAlert={false}
          variant={textualData?.content?.notification?.value?.variant}
          title={textualData?.content?.notification?.value?.title}
          text={<RichText html={textualData?.content?.notification?.value?.content} />}
        />
      )}

      <Controller
        control={control}
        name="revenue"
        render={({ field: { onChange, value, name } }) => (
          <RadioGroup
            aria-labelledby="revenue"
            name={name}
            direction="column"
            value={value}
            onValueChange={onChange}
            error={errors?.revenue?.message}>
            {textualData?.choice?.genericContentRadio?.value?.options?.map(
              ({ name, label }: { name: string; label: string }) => (
                <RadioTile key={name} value={name}>
                  {label}
                </RadioTile>
              ),
            )}
          </RadioGroup>
        )}
      />

      <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>
    </Layout.Content>
  );
};
